import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { buildPresets, type DateRange } from "./dateRange";

/**
 * A report's date range kept in the URL (?from=…&to=…), so a reload, the back button and a
 * bookmarked link all land on the same view. No params = the given preset; "all time" is explicit
 * empty params. `onChange` runs after each change (e.g. to reset a table to page 1).
 */
export function useDateRangeParam(defaultPresetId: "thisMonth" | "thisYear", onChange?: () => void): [DateRange, (next: DateRange) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const range: DateRange = useMemo(
    () =>
      searchParams.has("from") || searchParams.has("to")
        ? { from: searchParams.get("from") ?? "", to: searchParams.get("to") ?? "" }
        : buildPresets().find((p) => p.id === defaultPresetId)!.range,
    [searchParams, defaultPresetId],
  );
  const setRange = useCallback(
    (next: DateRange): void => {
      onChange?.();
      setSearchParams({ from: next.from, to: next.to }, { replace: true });
    },
    [onChange, setSearchParams],
  );
  return [range, setRange];
}
